import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface VoiceConfig {
  sttProvider: string;
  language: string;
  ttsService: string;
  accent: string;
  accentValue: string;
  gender: string;
  genderValue: string;
  voice: string;
}

interface MultilingualVoiceState {
  selectedLanguages: string[];
  voiceConfigs: Record<string, VoiceConfig>;
  sttProvider: string;
  availableGenders: Record<string, string[]>;
  availableAccents: Record<string, string[]>;
}

const initialState: MultilingualVoiceState = {
  selectedLanguages: [],
  voiceConfigs: {},
  sttProvider: 'Deepgram',
  availableGenders: {},
  availableAccents: {},
};

const multilingualVoiceSlice = createSlice({
  name: 'multilingualVoice',
  initialState,
  reducers: {
    setSttProvider: (state, action: PayloadAction<string>) => {
      state.sttProvider = action.payload;
    },
    addLanguage: (state, action: PayloadAction<string>) => {
      if (!state.selectedLanguages.includes(action.payload)) {
        state.selectedLanguages.push(action.payload);
        // Initialize voice configuration for the new language
        state.voiceConfigs[action.payload] = {
          sttProvider: state.sttProvider,
          language: action.payload,
          ttsService: 'Elevenlabs',
          accent: 'All',
          accentValue: '',
          gender: 'All',
          genderValue: '',
          voice: '',
        };
      }
    },
    removeLanguage: (state, action: PayloadAction<string>) => {
      state.selectedLanguages = state.selectedLanguages.filter(
        lang => lang !== action.payload
      );
      delete state.voiceConfigs[action.payload];
      delete state.availableGenders[action.payload];
      delete state.availableAccents[action.payload];
    },
    updateVoiceConfig: (
      state,
      action: PayloadAction<{
        language: string;
        field: keyof VoiceConfig;
        value: string;
      }>
    ) => {
      const { language, field, value } = action.payload;
      if (state.voiceConfigs[language]) {
        state.voiceConfigs[language] = {
          ...state.voiceConfigs[language],
          [field]: value,
        };
      }
    },
    setAvailableGendersForLanguage: (
      state,
      action: PayloadAction<{
        language: string;
        genders: string[];
      }>
    ) => {
      const { language, genders } = action.payload;
      state.availableGenders[language] = genders;
    },
    setAvailableAccentsForLanguage: (
      state,
      action: PayloadAction<{
        language: string;
        accents: string[];
      }>
    ) => {
      const { language, accents } = action.payload;
      state.availableAccents[language] = accents;
    },
    clearVoiceConfigs: state => {
      state.selectedLanguages = [];
      state.voiceConfigs = {};
      state.availableGenders = {};
      state.availableAccents = {};
    },
  },
});

export const {
  setSttProvider,
  addLanguage,
  removeLanguage,
  updateVoiceConfig,
  setAvailableGendersForLanguage,
  setAvailableAccentsForLanguage,
  clearVoiceConfigs,
} = multilingualVoiceSlice.actions;

export default multilingualVoiceSlice.reducer;
